import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const CREDIT_CARD_WIDTH = 85.6;
const CREDIT_CARD_HEIGHT = 53.98;

export default function Calibrate() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);

  const [cardWidthPx, setCardWidthPx] = useState(300);

  // =====================================================
  // DRAW CREDIT CARD
  // =====================================================
  const draw = () => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    const ctx = canvas.getContext("2d");

    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Background
    ctx.fillStyle = "#f8f9fb";
    ctx.fillRect(
      0,
      0,
      canvas.width,
      canvas.height
    );

    // Credit card aspect ratio
    const aspectRatio =
      CREDIT_CARD_WIDTH / CREDIT_CARD_HEIGHT;

    const cardHeightPx =
      cardWidthPx / aspectRatio;

    // Center card
    const x =
      (canvas.width - cardWidthPx) / 2;

    const y =
      (canvas.height - cardHeightPx) / 2;

    // =================================================
    // CARD BORDER
    // =================================================
    ctx.strokeStyle = "#222";
    ctx.lineWidth = 2;

    ctx.strokeRect(
      x,
      y,
      cardWidthPx,
      cardHeightPx
    );

    // =================================================
    // CORNER INDICATOR
    // =================================================
    ctx.fillStyle = "#999";

    ctx.beginPath();

    ctx.moveTo(
      x + cardWidthPx - 20,
      y + cardHeightPx
    );

    ctx.lineTo(
      x + cardWidthPx,
      y + cardHeightPx - 20
    );

    ctx.lineTo(
      x + cardWidthPx,
      y + cardHeightPx
    );

    ctx.closePath();

    ctx.fill();
  };

  // Redraw whenever slider changes
  useEffect(() => {
    draw();
  }, [cardWidthPx]);

  // =====================================================
  // SLIDER CHANGE
  // =====================================================
  const handleSlider = (e) => {
    setCardWidthPx(Number(e.target.value));
  };

  // =====================================================
  // SAVE CALIBRATION
  // =====================================================
  const handleStore = () => {
    const pixelsPerMm =
      cardWidthPx / CREDIT_CARD_WIDTH;

    localStorage.setItem(
      "pixel_in_mm",
      pixelsPerMm
    );

    navigate("/anticipation/play");
  };

  return (
    <>
      {/* =================================================
          PAGE
      ================================================= */}
      <div className="calibration-page">

        <div className="calibration-container">

          {/* =================================================
              TITLE
          ================================================= */}
          <h2 className="calibration-title">
            📏 Screen Calibration
          </h2>

          {/* =================================================
              CANVAS
          ================================================= */}
          <canvas
            ref={canvasRef}
            width={900}
            height={400}
            className="calibration-canvas"
          />

          {/* =================================================
              STEP 1
          ================================================= */}
          <p className="calibration-step">
            Step 1: Place the Credit Card in the box
          </p>

          {/* =================================================
              STEP 2
          ================================================= */}
          <p className="calibration-step step-two">
            Step 2: Adjust size using slider
          </p>

          {/* =================================================
              SLIDER
          ================================================= */}
          <div className="slider-wrapper">
            <input
              className="calibration-slider"
              type="range"
              min="200"
              max="600"
              step="1"
              value={cardWidthPx}
              onChange={handleSlider}
            />
          </div>

          {/* =================================================
              STEP 3
          ================================================= */}
          <p className="calibration-step step-three">
            Step 3: Click continue when matched
          </p>

          {/* =================================================
              CONTINUE BUTTON
          ================================================= */}
          <button
            type="button"
            onClick={handleStore}
            className="calibration-button"
          >
            Continue
          </button>

        </div>
      </div>

      {/* =====================================================
          STYLES
      ===================================================== */}
      <style>{`

        /* ===================================================
           PAGE
        =================================================== */

        .calibration-page {
          width: 100%;
          min-height: calc(100vh - 70px);

          box-sizing: border-box;

          padding: 25px 20px 40px;

          overflow-x: hidden;
        }


        /* ===================================================
           MAIN CONTAINER
        =================================================== */

        .calibration-container {
          width: 100%;
          max-width: 900px;

          margin: 0 auto;

          text-align: center;
          box-sizing: border-box;
        }


        /* ===================================================
           TITLE
        =================================================== */

        .calibration-title {
          font-size: 28px;

          margin: 0 0 20px;

          color: #111827;

          font-weight: 600;
        }


        /* ===================================================
           CANVAS
        =================================================== */

        .calibration-canvas {
          display: block;

          width: 100%;
          max-width: 900px;

          height: 400px;

          margin: 0 auto 30px;

          background: #ffffff;

          border: 1px solid #ddd;

          border-radius: 10px;

          box-sizing: border-box;
        }


        /* ===================================================
           STEPS
        =================================================== */

        .calibration-step {
          font-size: 16px;

          color: #111827;

          margin: 0 0 15px;

          line-height: 1.5;
        }

        .step-two {
          margin-top: 10px;
        }

        .step-three {
          margin-top: 5px;
          margin-bottom: 0;
        }


        /* ===================================================
           SLIDER WRAPPER
        =================================================== */

        .slider-wrapper {
          width: 100%;

          display: flex;

          justify-content: center;

          align-items: center;

          margin-top: 15px;

          margin-bottom: 20px;

          box-sizing: border-box;
        }


        /* ===================================================
           RANGE SLIDER
        =================================================== */

        .calibration-slider {
          width: 80%;

          max-width: 680px;

          height: 8px;

          margin: 0;

          padding: 0;

          appearance: none;

          -webkit-appearance: none;

          background: #cfcfcf;

          border-radius: 10px;

          outline: none;

          cursor: pointer;

          box-sizing: border-box;
        }


        /* ===================================================
           CHROME / EDGE - TRACK
        =================================================== */

        .calibration-slider::-webkit-slider-runnable-track {
          width: 100%;

          height: 8px;

          background: #cfcfcf;

          border-radius: 10px;

          border: none;
        }


        /* ===================================================
           CHROME / EDGE - THUMB
        =================================================== */

        .calibration-slider::-webkit-slider-thumb {
          appearance: none;

          -webkit-appearance: none;

          width: 16px;

          height: 16px;

          margin-top: -4px;

          border-radius: 50%;

          border: none;

          background: #1677ed;

          cursor: grab;

          box-sizing: border-box;
        }

        .calibration-slider::-webkit-slider-thumb:active {
          cursor: grabbing;
        }


        /* ===================================================
           FIREFOX - TRACK
        =================================================== */

        .calibration-slider::-moz-range-track {
          width: 100%;

          height: 8px;

          background: #cfcfcf;

          border-radius: 10px;

          border: none;
        }


        /* ===================================================
           FIREFOX - THUMB
        =================================================== */

        .calibration-slider::-moz-range-thumb {
          width: 16px;

          height: 16px;

          border-radius: 50%;

          border: none;

          background: #1677ed;

          cursor: grab;
        }

        .calibration-slider::-moz-range-thumb:active {
          cursor: grabbing;
        }


        /* ===================================================
           REMOVE FOCUS BLUE BOX
        =================================================== */

        .calibration-slider:focus {
          outline: none;
          box-shadow: none;
        }

        .calibration-slider:focus-visible {
          outline: none;
          box-shadow: none;
        }


        /* ===================================================
           BUTTON
        =================================================== */

        .calibration-button {
          background: #1e1b4b;

          color: #ffffff;

          padding: 10px 25px;

          border-radius: 8px;

          border: none;

          margin-top: 15px;

          cursor: pointer;

          font-size: 15px;

          font-weight: 600;

          transition: background 0.2s ease;
        }

        .calibration-button:hover {
          background: #29245f;
        }

        .calibration-button:active {
          transform: translateY(1px);
        }


        /* ===================================================
           MOBILE
        =================================================== */

        @media (max-width: 768px) {

          .calibration-page {
            padding: 20px 12px 30px;
          }

          .calibration-title {
            font-size: 24px;
          }

          .calibration-canvas {
            height: auto;

            aspect-ratio: 900 / 400;
          }

          .calibration-slider {
            width: 90%;
          }

          .calibration-step {
            font-size: 15px;
          }

        }

      `}</style>
    </>
  );
}